import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/utils";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Sparkles, Tag, FolderPlus, ShieldAlert, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const LIKELIHOOD_TONE = {
  High: "text-[#00E65B] border-[#00E65B]/40", Medium: "text-[#FFB800] border-[#FFB800]/40",
  Low: "text-red-500 border-red-500/40", "Needs More Information": "text-muted-foreground border-border",
};
const FIT_TONE = { Strong: "text-[#00E65B]", Moderate: "text-[#FFB800]", Weak: "text-red-500" };

export default function FinancingMatchesTab({ i, onChanged }) {
  const [matches, setMatches] = useState([]);
  const [narrative, setNarrative] = useState("");
  const [tagged, setTagged] = useState(i.tagged_finance_offer || null);
  const [status, setStatus] = useState(i.financing_match_status);
  const [busy, setBusy] = useState(false);
  const [acting, setActing] = useState(null);

  const load = () => api.get(`/initiatives/${i.id}/green-finance-matches`).then((r) => {
    setMatches(r.data.matches || []); setTagged(r.data.tagged_finance_offer); setStatus(r.data.match_status);
  }).catch(() => {});
  useEffect(() => { load(); }, [i.id]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/initiatives/${i.id}/green-finance-match/run`);
      setMatches(r.data.matches || []); setNarrative(r.data.narrative); setStatus(r.data.match_status);
      toast.success("Matches generated"); onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const tag = async (offerId) => {
    setActing(offerId);
    try {
      const r = await api.post(`/initiatives/${i.id}/tag-finance-offer`, { offer_id: offerId });
      setTagged(r.data.tagged_finance_offer); setStatus("tagged");
      toast.success(`Tagged ${r.data.tagged_finance_offer.offer_name}`); onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setActing(null); }
  };

  const createCase = async (offerId) => {
    setActing(offerId);
    try {
      if ((tagged?.offer_id) !== offerId) await api.post(`/initiatives/${i.id}/tag-finance-offer`, { offer_id: offerId });
      const r = await api.post(`/initiatives/${i.id}/create-financing-case`, {});
      setStatus("case_created");
      toast.success(`Financing case created: ${r.data.case_name}`);
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setActing(null); }
  };

  return (
    <div className="space-y-4" data-testid="init-financing-matches-tab">
      <div className="rounded-sm border border-border bg-surface-hover/40 p-2.5 text-[11px] text-muted-foreground flex gap-2">
        <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5 text-[#FFB800]" />
        Financing matches and approval likelihood are indicative platform assessments only. They do not represent bank approval, credit approval, or financial advice. Final eligibility, pricing, terms and approval are determined by the bank.
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-muted-foreground">
          {tagged ? <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#00E65B]" /> Tagged: <strong>{tagged.bank_name} {tagged.offer_name}</strong></span>
            : "Run AI matching against the approved green-finance catalog."}
        </div>
        <Button variant="primary" size="sm" onClick={run} disabled={busy} data-testid="run-match-btn">
          <Sparkles className="h-4 w-4" /> {busy ? "Matching…" : matches.length ? "Re-run match" : "Find green financing options"}
        </Button>
      </div>

      {narrative && <div className="card-flat p-3 text-sm" data-testid="match-narrative">{narrative}</div>}

      <div className="space-y-3">
        {matches.map((m) => (
          <div key={m.offer_id} className="card-flat p-4" data-testid={`match-card-${m.offer_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-semibold text-sm">{m.bank_name} · {m.offer_name}</h4>
                  <Badge variant="outline" className="text-[9px]">{m.financing_type?.replace(/_/g, " ")}</Badge>
                  <span className={`text-[10px] font-medium ${FIT_TONE[m.estimated_fit]}`}>{m.estimated_fit} fit</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{m.why_it_matches}</p>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-2xl font-bold" data-testid={`match-score-${m.offer_id}`}>{m.match_score}<span className="text-xs text-muted-foreground">/100</span></div>
                <Badge variant="outline" className={`text-[9px] mt-1 ${LIKELIHOOD_TONE[m.approval_likelihood_label]}`}>{m.approval_likelihood_label}</Badge>
              </div>
            </div>

            <div className="text-[10px] text-muted-foreground mt-1">Match Score: {m.match_score}/100 — appears aligned with this offer based on available information. This is not a bank approval decision.</div>

            <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5 mt-3">
              {Object.entries(m.sub_scores).map(([k, v]) => (
                <div key={k} className="text-center border border-border rounded-sm p-1.5">
                  <div className="font-mono text-xs font-bold">{v}</div>
                  <div className="text-[8px] text-muted-foreground capitalize leading-tight">{k.replace(/_/g, " ")}</div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between gap-3 mt-3 flex-wrap text-xs">
              <div className="text-muted-foreground">
                {m.indicative_rate != null ? <>Indicative rate: <strong>{m.indicative_rate}%</strong></> : <span className="italic">Interest rate not published — enter manually or request a quote.</span>}
                {m.indicative_tenure && <> · Tenure: {m.indicative_tenure}</>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={acting === m.offer_id} onClick={() => tag(m.offer_id)} data-testid={`tag-offer-${m.offer_id}`}>
                  <Tag className="h-3.5 w-3.5" /> {tagged?.offer_id === m.offer_id ? "Tagged" : "Tag offer"}
                </Button>
                <Button size="sm" variant="primary" disabled={acting === m.offer_id} onClick={() => createCase(m.offer_id)} data-testid={`create-case-${m.offer_id}`}>
                  <FolderPlus className="h-3.5 w-3.5" /> Create financing case
                </Button>
              </div>
            </div>

            {m.missing_requirements?.length > 0 && (
              <div className="mt-2 text-[11px] text-[#FFB800]">Missing: {m.missing_requirements.join(", ")}</div>
            )}
          </div>
        ))}
        {!matches.length && !busy && <div className="text-sm text-muted-foreground text-center py-6 border border-dashed border-border rounded-sm">No matches yet. Click “Find green financing options”.</div>}
      </div>
    </div>
  );
}
